import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {Flex, Grid, GridProps, Image, Text, useDisclosure, useToast} from '@chakra-ui/react';
import {AnimatePresence, motion} from 'framer-motion';

import {px} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {useAccessToken, useRegionSettings} from '@eksab/hooks';
import {getCountry} from '@eksab/util';

import {AuthModal} from '@eksab/features/auth/modals/AuthModal';

import {QuestionPoints, PrizeIcon} from './';
import {useGuestStore, useAttachUserPrizeMutation} from '../hooks';
import {useTriviaStore} from '../state';
import giftIcon from '../icons/giftIcon2.png';
import {games} from '@eksab/features/play/const';

export function Winner(props: GridProps) {
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const {data: region} = useRegionSettings();
  const isLoggedIn = useAccessToken().isSuccess;
  const userPrizeID = useTriviaStore((state) => state.prizeId);
  const challenge = useTriviaStore((state) => state.challenge!);
  const hasAttachedGuestPrize = useTriviaStore((state) => state.hasAttachedGuestPrize);
  const game = games[challenge.game];

  const isWE = game.name == 'trivia-misc-we';

  if (!challenge.prize) return null;

  return (
    <Grid
      flex={1}
      px={px}
      w="fit-content"
      templateColumns={{base: '1fr', lg: '1fr 1fr'}}
      columnGap="16"
      placeItems="center"
      alignContent={{base: 'start', lg: 'center'}}
      textAlign="center"
      {...props}
    >
      <PrizeIcon
        type={challenge.prize.type}
        gridRow={{base: '2', lg: '1 / 5'}}
        w={{base: 40, lg: 60}}
        h="auto"
        mb={{base: 6, lg: 0}}
      />

      {!isWE && <QuestionPoints gridRow={{base: '1', lg: 'auto'}} mb="6" />}

      <Text fontSize="md" fontWeight={[500, null, null, 600]} mb={{base: 1, lg: 2}}>
        {t(`trivia.score.${game.topic}.winner.subheading`)}
      </Text>

      <Text fontSize={['22px', null, null, '34px']} fontWeight="800" mb={{base: 3, lg: 6}}>
        {isWE ? (
          t(`trivia.score.we.heading.5`)
        ) : (
          <>
            {t(`trivia.score.${game.topic}.winner.heading`)} (
            {challenge.prize.type === 'physical'
              ? challenge.prize.name
              : `${format(challenge.prize.amount || 0)}  ${challenge.prize.type === 'cash' ? region?.currency ?? '' : t('app.coins')}`}
            )
          </>
        )}
      </Text>

      {!isWE && (
        <AnimatePresence mode="wait">
          <motion.div
            key={userPrizeID ?? 'loading'}
            initial={{x: -150, opacity: 0}}
            animate={{
              x: 0,
              opacity: 1,
              transition: {
                type: 'spring',
                stiffness: 200,
                duration: 0.6,
              },
            }}
            exit={{
              x: 50,
              opacity: 0,
              transition: {
                ease: 'easeOut',
                duration: 0.3,
              },
            }}
          >
            {isLoggedIn ? (
              hasAttachedGuestPrize ? (
                <PrizeLoading />
              ) : userPrizeID ? (
                <ClaimPrizeButton />
              ) : (
                <PrizeLoading />
              )
            ) : (
              <ClaimPrizeButton />
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </Grid>
  );
}

const ClaimPrizeButton = () => {
  const toast = useToast();
  const router = useRouter();
  const format = useIntl().formatNumber;
  const authModalDisclosure = useDisclosure();

  const t = useTranslate();
  const {data: region} = useRegionSettings();
  const attemptID = useTriviaStore((state) => state.attemptId);
  const userPrizeID = useTriviaStore((state) => state.prizeId);
  const prize = useTriviaStore((state) => state.challenge?.prize!);
  const attachUserPrizeMutation = useAttachUserPrizeMutation(attemptID);

  const onClaimPrize = () => {
    mixpanel?.track('Button Clicked', {button: 'Redeem Prize - Trivia Challenge Score'});

    if (!userPrizeID) {
      authModalDisclosure.onOpen();
      return;
    }

    const state = useTriviaStore.getState();
    const prevAppLocale = state.prevAppLanguage ? `${state.prevAppLanguage}-${getCountry(router.locale)}` : undefined;
    router.replace(`/profile/prizes/${userPrizeID}`, undefined, {locale: prevAppLocale});
  };

  const onAuthModalClose = (me: Me | undefined) => {
    if (!me) return;

    const guestID = useGuestStore.getState().id!;
    attachUserPrizeMutation.mutate(
      {id: guestID},
      {
        onSuccess: () => {
          authModalDisclosure.onClose();
          useTriviaStore.setState({hasAttachedGuestPrize: true});
        },
        onError: (e) => toast({title: e.response?.data.message ?? t('app.errors.try-again'), status: 'error'}),
      },
    );
  };

  return (
    <Flex
      align="center"
      justify="space-between"
      w="300px"
      px="4"
      my="3"
      rounded="24px"
      bg="linear-gradient(359.92deg, #EC4B72 0.07%, #F4A771 99.93%);"
      cursor="pointer"
      onClick={onClaimPrize}
    >
      <Flex flexDir="column" px="2" py="3" align="start">
        <Flex py="0.5" px="2" rounded="60px" bg="rgba(0, 0, 0, 0.41);" gap="1">
          <PrizeIcon width="15px" height="auto" type={prize?.type} />
          <Flex gap="1">
            <Text color="white" fontSize="sm" fontWeight="700">
              {prize?.type === 'physical' ? prize?.name : format(prize?.amount || 0)}
            </Text>
            <Text color="#FED44C" fontSize="sm" fontWeight="700">
              {prize?.type === 'cash' ? region?.currency ?? '' : prize?.type === 'coins' && '₡'}
            </Text>
          </Flex>
        </Flex>
        <Text color="white" fontSize="md" fontWeight="800">
          {t('trivia.prize.redeem')}{' '}
          {prize?.type === 'cash' ? t('trivia.prize.money') : prize?.type === 'physical' ? prize?.name : t('app.coins')}
        </Text>
      </Flex>

      <Image src={giftIcon.src} alt="" objectFit="contain" width="64px" height="auto" />

      <AuthModal
        {...authModalDisclosure}
        route="register"
        canSwitchRoute={false}
        onClose={onAuthModalClose}
        closeOnEsc={false}
        closeOnOverlayClick={false}
      />
    </Flex>
  );
};

const PrizeLoading = () => {
  const t = useTranslate();

  return (
    <Flex flexDir="column" gap="1" align="center" width="300px">
      <Image src={giftIcon.src} alt="" objectFit="contain" width="64px" height="auto" />
      <Flex gap="1">
        <Text color="#5AC57B" fontSize="18px" fontWeight="700">
          {t('trivia.prize.loading')}
        </Text>
        <motion.div
          key="loading"
          style={{width: '30px', display: 'flex', justifyContent: 'space-around'}}
          variants={{
            initial: {
              transition: {
                staggerChildren: 0.2,
              },
            },
            animate: {
              transition: {
                staggerChildren: 0.2,
              },
            },
          }}
          initial="initial"
          animate="animate"
        >
          {Array.from({length: 3}).map((_, i) => (
            <motion.span
              key={i}
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#5AC57B',
                borderRadius: '20px',
              }}
              transition={{duration: 0.5, yoyo: Infinity, ease: 'easeInOut'}}
              variants={{
                initial: {
                  y: '100%',
                  opacity: 0.6,
                },
                animate: {
                  y: '140%',
                  opacity: 0.9,
                },
              }}
            />
          ))}
        </motion.div>
      </Flex>
    </Flex>
  );
};
